import { Shell } from "@/components/layout/Shell";
import { useAuth } from "@/hooks/use-auth";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Loader2, Building2, Save } from "lucide-react";
import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";

interface VistoriadoraConfig {
  id: string;
  nome: string;
  fecharLotePorCliente: boolean;
  fechamentoAutomatico: boolean;
}

function VistoriadoraRow({ v, token }: { v: VistoriadoraConfig; token: string }) {
  const { toast } = useToast();
  const [porCliente, setPorCliente] = useState<boolean>(v.fecharLotePorCliente);
  const [fechamentoAuto, setFechamentoAuto] = useState<boolean>(v.fechamentoAutomatico);

  const dirty =
    porCliente !== v.fecharLotePorCliente ||
    fechamentoAuto !== v.fechamentoAutomatico;

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/owner/vistoriadoras-config/${v.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          fechamentoAutomatico: fechamentoAuto,
          fecharLotePorCliente: porCliente,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message ?? "Erro ao salvar");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/owner/vistoriadoras-config"] });
      toast({ title: `${v.nome} — configuração salva` });
    },
    onError: (err: Error) => {
      toast({ title: err.message, variant: "destructive" });
    },
  });

  return (
    <Card className="border-none shadow-md">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center">
              <Building2 className="w-4 h-4 text-slate-500" />
            </div>
            <div>
              <CardTitle className="text-sm font-semibold text-slate-900">{v.nome}</CardTitle>
              <CardDescription className="text-xs mt-0">
                {fechamentoAuto ? "Fechamento automático" : "Fechamento manual"}
              </CardDescription>
            </div>
          </div>
          {dirty && (
            <Badge variant="secondary" className="bg-amber-100 text-amber-700 text-xs">
              Alterado
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Fechamento automático */}
        <div className="flex items-center justify-between pt-1 border-t border-slate-100">
          <div>
            <p className="text-xs font-medium text-slate-700">Fechamento automático de lote</p>
            <p className="text-xs text-slate-500">Quando desligado, o lote só fecha manualmente</p>
          </div>
          <Switch
            checked={fechamentoAuto}
            onCheckedChange={(val) => {
              setFechamentoAuto(val);
              if (val) setPorCliente(false);
            }}
          />
        </div>

        {/* Fechar por cliente (só aparece se fechamento manual) */}
        {!fechamentoAuto && (
          <div className="flex items-center justify-between pt-1">
            <div>
              <p className="text-xs font-medium text-slate-700">Fechar lote por cliente</p>
              <p className="text-xs text-slate-500">Cria lotes separados por empresa-cliente</p>
            </div>
            <Switch
              checked={porCliente}
              onCheckedChange={setPorCliente}
            />
          </div>
        )}

        {/* Botão salvar */}
        <div className="flex justify-end pt-1">
          <Button
            size="sm"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
          >
            {mutation.isPending ? (
              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
            ) : (
              <Save className="w-3.5 h-3.5 mr-1.5" />
            )}
            Salvar
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function AdminVistoriadoras() {
  const { profile, session, loading } = useAuth();
  const [, navigate] = useLocation();
  const isOwner = profile?.roles?.includes("owner") ?? false;

  const { data: vistoriadoras, isLoading } = useQuery<VistoriadoraConfig[]>({
    queryKey: ["/api/owner/vistoriadoras-config"],
    queryFn: async () => {
      const res = await fetch("/api/owner/vistoriadoras-config", {
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      if (!res.ok) throw new Error("Erro ao carregar");
      return res.json();
    },
    enabled: !!session && isOwner,
  });

  if (loading) {
    return (
      <Shell>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      </Shell>
    );
  }

  if (!isOwner) {
    navigate("/");
    return null;
  }

  return (
    <Shell>
      <div className="mb-8">
        <h1 className="text-3xl font-bold font-display text-slate-900">Regras por Vistoriadora</h1>
        <p className="text-muted-foreground mt-1">
          Configure o split de receita, laudos gratuitos e fechamento de lote para cada vistoriadora
        </p>
      </div>

      <div className="max-w-2xl space-y-4">
        {isLoading ? (
          Array(3).fill(0).map((_, i) => <Skeleton key={i} className="h-64 rounded-xl" />)
        ) : !vistoriadoras || vistoriadoras.length === 0 ? (
          <Card className="border-none shadow-md">
            <CardContent className="py-12 text-center text-slate-500">
              <Building2 className="w-8 h-8 mx-auto mb-3 text-slate-300" />
              <p className="font-medium">Nenhuma vistoriadora cadastrada</p>
            </CardContent>
          </Card>
        ) : (
          vistoriadoras.map((v) => (
            <VistoriadoraRow key={v.id} v={v} token={session?.access_token ?? ""} />
          ))
        )}
      </div>
    </Shell>
  );
}
